import React from 'react';
import { IconFolderPlus, IconUpload } from '@tabler/icons-react';
import { DocumentDto } from 'src/api/FiretrolApiClient';
import { useDocument } from 'src/api/queries/documents/useDocument';
import { useDeleteFolder } from 'src/api/queries/documents/useFolderMutations';
import { FolderTree } from 'src/app/documents/FolderTree';
import { ROOT_FOLDER_ID } from 'src/app/documents/documentConstants';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { BrowserLayout } from 'src/design/organisms/BrowserLayout/BrowserLayout';
import { Header } from 'src/design/organisms/Header';
import { useAuth } from 'src/hooks/useAuth';
import { useJobs } from 'src/hooks/useJobs';
import { DocumentBrowser } from './components/DocumentBrowser';
import { DocumentDetails } from './components/DocumentDetails';
import { FolderDialog } from './components/FolderDialog';
import classes from './index.module.css';
import { useJobDocuments } from './useJobDocuments';

export const Documents: React.FC = () => {
  const { selectedJob } = useJobs();
  const { isAdmin } = useAuth();
  const [isFolderOpen, setIsFolderOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [sidebarOpened, setSidebarOpened] = React.useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<
    string | null
  >(null);
  const openFilePicker = React.useRef<(() => void) | null>(null);

  const {
    folders,
    documents,
    currentFolderId,
    masterId,
    parentFolder,
    subfolders,
    breadcrumbs,
    isSelectedLocked,
    documentsLoading,
    selectFolder,
    resetToRoot,
  } = useJobDocuments(selectedJob?.id);

  const jobId = selectedJob?.id;
  const { data: selectedDocument } = useDocument(jobId, selectedDocumentId);
  const deleteFolderMutation = useDeleteFolder(jobId);

  const closeDetails = React.useCallback(() => setSelectedDocumentId(null), []);

  const onFolderSelected = (folderId: string) => {
    closeDetails();
    setSidebarOpened(false);
    selectFolder(folderId);
  };

  const deleteFolder = async () => {
    if (!selectedJob || currentFolderId === ROOT_FOLDER_ID) return;
    // Rethrows so the confirmation modal stays open for a retry.
    await deleteFolderMutation.mutateAsync(currentFolderId);
    resetToRoot();
    closeDetails();
  };

  if (!selectedJob) return null;

  return (
    <div className={classes.page}>
      <Header
        title="Documents"
        breadcrumbProps={{
          items: jobBreadcrumbs(selectedJob, undefined, 'Documents'),
        }}
        helpText="Browse, upload, and organize documents and folders for this job."
        actions={
          <>
            <SecondaryButton
              leftSection={<IconFolderPlus size={16} />}
              disabled={isSelectedLocked}
              onClick={() => {
                closeDetails();
                setIsFolderOpen(true);
              }}
            >
              New Folder
            </SecondaryButton>
            <CreateButton
              leftSection={<IconUpload size={16} />}
              onClick={() => openFilePicker.current?.()}
            >
              Upload
            </CreateButton>
          </>
        }
      />

      <BrowserLayout
        sidebar={
          <FolderTree
            folders={folders}
            selectedFolderId={currentFolderId}
            onFolderSelected={onFolderSelected}
          />
        }
        sidebarOpened={sidebarOpened}
        onSidebarClose={() => setSidebarOpened(false)}
        asideOpen={!!selectedDocument}
        onAsideClose={closeDetails}
        aside={
          selectedDocument && (
            <DocumentDetails
              jobId={selectedJob.id}
              document={selectedDocument}
              canDelete={!selectedDocument.isLocked || isAdmin}
              onClose={closeDetails}
              onDeleted={closeDetails}
            />
          )
        }
      >
        <DocumentBrowser
          jobId={selectedJob.id}
          currentFolderId={currentFolderId}
          currentFolder={parentFolder}
          breadcrumbs={breadcrumbs}
          subfolders={subfolders}
          documents={documents}
          isSelectedLocked={isSelectedLocked}
          canDeleteFolder={!isSelectedLocked || isAdmin}
          isLoading={documentsLoading}
          selectedDocumentId={selectedDocumentId}
          openFilePicker={openFilePicker}
          onNavigate={onFolderSelected}
          onSelectDocument={(document: DocumentDto) =>
            setSelectedDocumentId(document.id)
          }
          onEditFolder={() => {
            closeDetails();
            setIsEditOpen(true);
          }}
          onDeleteFolder={deleteFolder}
          onOpenSidebar={() => setSidebarOpened(true)}
        />
      </BrowserLayout>

      <FolderDialog
        edit
        isLocked={isSelectedLocked}
        masterId={masterId}
        parentFolder={parentFolder}
        isOpen={isEditOpen}
        currentFolderId={currentFolderId}
        onClose={() => setIsEditOpen(false)}
      />
      <FolderDialog
        masterId={masterId}
        parentFolder={parentFolder}
        isLocked={isSelectedLocked}
        isOpen={isFolderOpen}
        currentFolderId={currentFolderId}
        onClose={() => setIsFolderOpen(false)}
      />
    </div>
  );
};
